/**
 * Utilidades para la optimización de imágenes.
 * Transforma URLs de Supabase en URLs de proxy del backend con opciones de redimensionado.
 */

export interface ImageOptions {
    width?: number;
    height?: number;
    quality?: number;
    fit?: 'cover' | 'contain' | 'fill' | 'inside' | 'outside';
}

/**
 * Convierte una URL de Supabase Storage en una URL optimizada mediante el proxy del backend.
 * 
 * @param url URL original de la imagen
 * @param options Opciones de transformación (ancho, alto, calidad)
 * @returns URL transformada o la original si no es de Supabase
 */
export const getOptimizedImageUrl = (url: string | null | undefined, options: ImageOptions = {}): string => {
    if (!url) return '';

    // Si no es una URL de Supabase, retornar original
    if (!url.includes('supabase.co')) {
        return url;
    }

    try {
        // Ejemplo: https://xyz.supabase.co/storage/v1/object/public/products-images/product1.webp
        const urlObj = new URL(url);
        const pathParts = urlObj.pathname.split('/');

        // El bucket es el siguiente a "public" (o "authenticated")
        const publicIndex = pathParts.indexOf('public');
        const privateIndex = pathParts.indexOf('authenticated');
        const rootIndex = publicIndex !== -1 ? publicIndex : privateIndex;

        if (rootIndex === -1 || rootIndex + 2 >= pathParts.length) {
            return url;
        }

        const bucket = pathParts[rootIndex + 1];
        const path = pathParts.slice(rootIndex + 2).join('/');

        // Construir URL del proxy del backend
        // Usamos ruta relativa /api/media/ si ya estamos en el mismo dominio
        // O podemos extraer el dominio base del entorno si es necesario
        const queryParams = new URLSearchParams();
        if (options.width) queryParams.set('w', options.width.toString());
        if (options.height) queryParams.set('h', options.height.toString());
        if (options.quality) queryParams.set('q', options.quality.toString());
        if (options.fit) queryParams.set('fit', options.fit);

        const queryString = queryParams.toString();
        const proxyUrl = `/api/media/${bucket}/${path}${queryString ? `?${queryString}` : ''}`;

        return proxyUrl;
    } catch (error) {
        console.error('Error parsing image URL:', error);
        return url;
    }
};
